import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Subject, takeUntil } from 'rxjs';
import { combineToDate, inBetween, recalculateNRICDriverAge, splitNRIC } from '../../helpers/service.helper';
import { NricService } from '@services/nric/nric.service';
import { SharedModule } from '@shared/shared.module';
import { FormatDecimalPipe } from '@shared/pipes/format-decimal/format-decimal.pipe';
import { RemoveCommaPipe } from '@shared/pipes/remove-comma/remove-comma.pipe';
import { CALCULATE_PREMIUM, CALCULATE_PREMIUM_PAYLOAD, STORE_AV_IND_OVERRIDE, STORE_COVERAGE_TYPE, STORE_COVERAGE_VEHICLE_DETAILS } from '@store/sat-motor/sat-motor.action';

@Component({
  selector: 'app-driver-name',
  standalone: true,
  imports: [
    SharedModule,
  ],
  templateUrl: './driver-name.component.html',
  styleUrl: './driver-name.component.scss'
})
export class DriverNameComponent implements OnInit, OnChanges {
  /*
  * pending type coverage to connect to BE to get the default driver name, driver age and driver id list
  * pending extra coverage to complete
  */
  @Input() coverageFmGroup!: FormGroup;
  @Input() isComprehensive = false;
  @Input() isThirdParty = false;
  @Input() isThirdPartyFireTheft = false;
  @Input() defaultDriver: any;
  @Input() parentForm: FormGroup;
  @Input() partnerInfo: any;
  @Output() selectExtraCover = new EventEmitter<any>();
  @Output() removeExtraCover = new EventEmitter<any>();
  @Output() selectWaiver = new EventEmitter<any>();
  @Output() selectAllDriver = new EventEmitter<any>();
  @Output() calculatePremium = new EventEmitter<any>();

  userInfo: any;
  driverNameForm: FormGroup;
  driverNameList: any;
  isDisableAddDriverBtn: boolean;
  /*
    'PRDCTA' = 'PRIVATE CARS EXCLUDING GOODS',
    'PRDCTB' = 'PRIVATE CARS INCLUDING GOODS',
    'PRDCTC' = 'PRIVATE CARS COMPANY REGISTERED',
    'PRDCTD' = 'MOTOR CYCLE',
    'PRDCTE' = 'MOTORCYCLE PLUS'
  */
  product: any;
  driverNameDetails: any;
  vehicleUnNamedDrvrInd: boolean;
  vehicleAlldrvrInd: boolean;

  unsubscribe$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private nricService: NricService
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.driverNameForm && changes?.["defaultDriver"]) {
      if(this.isComprehensive && (this.isPrivateCarsIncludingGoods || this.isPrivateCarsCompanyRegistered) && this.driverNameForm.get("allDriverInd").value == false) {
        return;
      }
      this.defaultDriver = {
        ...this.defaultDriver,
        driverAge: this.getDriverAge(this.partnerInfo.clientAge ,this.defaultDriver.driverAge)
      };

      this.rows.at(0).get('driverName').setValue(this.defaultDriver.cngeRiskDrvrPkDto.driverName);
      this.rows.at(0).get('driverId').setValue(this.defaultDriver.driverId);
      this.rows.at(0).get('driverAge').setValue(this.defaultDriver.driverAge);
      console.log("changed default driver: ", this.defaultDriver);
    }
  }
  
  ngOnInit() {
    this.userInfo = this.store.selectSnapshot<any>(state => state.UserState.user);
    this.product = this.store.selectSnapshot<String>(state => state.SatMotorState.motor.step1.issuance.issuance.productCode);
    let extraCoverLevelDriverNameList = this.store.selectSnapshot<any>(state => state.SatMotorState.motor.step3.coverage?.extracover?.driverNameDetails?.rows);
    let premiumLevelDriverNameList = this.store.selectSnapshot<any>(state => state.SatMotorState?.motor?.step3?.coverage?.coveragePremium?.cngeNote?.riskList?.[0]?.cngeRiskDrvrDtoList)
      ?.filter(d => d.cngeRiskDrvrPkDto.driverType === "NORM")
      ?.map(a => {
        return {
          driverName: a.cngeRiskDrvrPkDto.driverName,
          driverId: a.driverId,
          driverAge: a.driverAge,
          driverSeq: a.driverSeq
        }
      });
    this.driverNameDetails = extraCoverLevelDriverNameList ?? premiumLevelDriverNameList;
    this.vehicleUnNamedDrvrInd = this.store.selectSnapshot<boolean>(state => state.SatMotorState.motor.step3.coverage?.extracover?.driverNameDetails?.vehicleUnNamedDrvrInd);
    this.vehicleAlldrvrInd = this.store.selectSnapshot<boolean>(state => state.SatMotorState.motor.step3.coverage?.extracover?.driverNameDetails?.vehicleAlldrvrInd);

    this.driverNameForm = this.fb.group({
      allDriverInd: [this.defaultAllDriverInd()],
      waiverInd: [this.defaultWaiverInd()],
      rows: this.fb.array([])
    });
    
    if (this.driverNameDetails) {
      this.driverNameDetails.forEach((drv, index) => {
        let isValidNric;
        try {
          isValidNric = this.nricService.isNRICValid(drv.driverId);
        } catch (err) {
          isValidNric = false;
        }

        if(this.isComprehensive && (this.isPrivateCarsIncludingGoods || this.isPrivateCarsCompanyRegistered) && this.driverNameForm.get("allDriverInd").value == false) {
          this.rows.push(this.fb.group({
            driverName: [{ value: drv.driverName, disabled: false }],
            driverId: [{ value: drv.driverId ?? 'N/A', disabled: false }],
            driverAge: [{ value: drv.driverAge || 'N/A', disabled: isValidNric }]
          }));        
        } else {
          this.rows.push(this.fb.group({
            driverName: [{ value: drv.driverName, disabled: index === 0 }],
            driverId: [{ value: drv.driverId ?? 'N/A', disabled: index === 0 }],
            driverAge: [{ value: drv.driverAge || 'N/A', disabled: index === 0 || isValidNric}]
          }));
        }
        
      });
    }else {
      this.initDefaultDriverInfo();
    }

    this.parentForm.controls["driverNameDetails"] = this.driverNameForm;

    this.isDisableAddDriverBtn = this.disableAddDriver();
    this.coverageFmGroup.controls["driverNameForm"] = this.driverNameForm;

  }

  initDefaultDriverInfo() {
    this.rows.push(this.fb.group({
      driverName: [{ value: this.defaultDriver?.cngeRiskDrvrPkDto?.driverName ? this.defaultDriver.cngeRiskDrvrPkDto.driverName : '', disabled: true }],
      driverId: [{ value: this.defaultDriver?.driverId ? this.defaultDriver.driverId : '', disabled: true }],
      driverAge: [{ value: this.defaultDriver?.driverAge ? this.getDriverAge(this.partnerInfo.clientAge, this.defaultDriver.driverAge)  : 'N/A', disabled: true }]
    }));
  }

  defaultAllDriverInd() {
    if (this.vehicleAlldrvrInd === undefined || this.vehicleAlldrvrInd === null) {
      if (this.product === '080200' || this.product === '080300') {
        this.selectExtraCover.emit(true);
        return true;
      } else {
        this.selectExtraCover.emit(false);
        return false;
      }
    }else {
      return this.vehicleAlldrvrInd;
    }

  }

  defaultWaiverInd() {
    if (this.vehicleUnNamedDrvrInd === undefined || this.vehicleUnNamedDrvrInd === null) {
      if (this.userInfo.callCenter) {
        return true;
      } else {
        return false;
      }
    }else {
      return this.vehicleUnNamedDrvrInd;
    }

  }

  addDriver() {
    let driverNameRow;

    driverNameRow = this.fb.group({
      driverName: [''],
      driverId: [''],
      driverAge: ['']
    });

    this.rows.push(driverNameRow);

    this.isDisableAddDriverBtn = this.disableAddDriver();

    if(this.rows.length > 2) {
      this.store.dispatch(new STORE_AV_IND_OVERRIDE(true));
      this.calculatePremium.emit();
    }

    console.log("rows: ", this.rows);
  }

  deleteDriver(index) {
    let prevRowsLength = this.rows.length;
    this.rows.removeAt(index);

    this.isDisableAddDriverBtn = this.disableAddDriver();

    if(prevRowsLength > 2) {
      this.calculatePremium.emit();
    }
  }

  allDriverOnChange() {
    let allDriverInd = this.driverNameForm.controls['allDriverInd'].value;

    if (allDriverInd) {
      this.deleteSecondRowOnwards();
      this.rows.at(0).disable();
      this.rows.at(0).get('driverName').setValue(this.defaultDriver.cngeRiskDrvrPkDto.driverName);
      this.rows.at(0).get('driverId').setValue(this.defaultDriver.driverId);
      this.rows.at(0).get('driverAge').setValue(this.defaultDriver.driverAge);
      this.selectExtraCover.emit("M02");
    } else {
      this.rows.at(0).get('driverName').reset();
      this.rows.at(0).get('driverName').enable();

      if (this.isPrivateCarsIncludingGoods || this.isPrivateCarsCompanyRegistered) {
        this.rows.at(0).get('driverId').reset();
        this.rows.at(0).get('driverId').enable();

        this.rows.at(0).get('driverAge').reset();
        this.rows.at(0).get('driverAge').enable();
      }
      this.removeExtraCover.emit("M02");
    }
    this.isDisableAddDriverBtn = this.disableAddDriver();
    this.selectAllDriver.emit(allDriverInd);

  }

  waiverIndOnChange() {
    let waiverInd = this.driverNameForm.controls['waiverInd'].value;

    if (waiverInd) {
      this.deleteSecondRowOnwards();
    }
    this.isDisableAddDriverBtn = this.disableAddDriver();
    this.selectWaiver.emit(waiverInd);
  }

  deleteSecondRowOnwards() {
    for (let i = this.rows.length - 1; i >= 1; i--) {
      this.rows.removeAt(i);
    }
  }

  disableAddDriver() {
    let allDriverInd = this.driverNameForm.controls['allDriverInd'].value;
    let waiverInd = this.driverNameForm.controls['waiverInd'].value;
    let driverListLength = this.rows.length;
    let disable = false;

    if (allDriverInd || waiverInd) {
      disable = true;
    } else {
      switch (this.product) {
        case '080100':
          disable = driverListLength >= 3 ? true : false;
          break;

        case '080200':
        case '080300':
          disable = driverListLength >= 2 ? true : false;
          break;
        default:
          disable = true;
          break;
      }
    }
    return disable;
  }

  onChangeDriverId(index) {
    let driverId = this.rows.at(index).get('driverId').value;
    let isValidNric;

    try {
      isValidNric = this.nricService.isNRICValid(driverId);
    } catch (err) {
      isValidNric = false;
    }

    if (driverId && isValidNric) {
      const [input, year, month, day, birthPlace, ...rest] = splitNRIC(driverId);
      const driverBirthDate = combineToDate(year, month, day);
      const todayDate = new Date();

      if (driverBirthDate.getFullYear() > todayDate.getFullYear()) {
        throw new Error('Invalid Driver ID');
      } else {
        let driverAge = recalculateNRICDriverAge(driverId);
        this.rows.at(index).get('driverAge').disable();
        this.rows.at(index).get('driverAge').setValue(driverAge);
      }
    } else {
      this.rows.at(index).get('driverAge').enable();
      this.rows.at(index).get('driverAge').setValue("");
    }



  }

  formatAgeAndId(value) {
    if(value == 0) {
      return 'N/A';
    }
    return value;
  }

  getDriverAge(cpAge, driverAge) {
    if(!driverAge) {
      return "N/A";
    }

    if(cpAge == driverAge) {
      return driverAge;
    }else {
      return cpAge;
    }
  }

  get isPrivateCarsExcludingGoods() {
    return this.product === '080100';
  }

  get isPrivateCarsIncludingGoods() {
    return this.product === '080200';
  }

  get isPrivateCarsCompanyRegistered() {
    return this.product === '080300';
  }

  get rows() {
    return this.driverNameForm.get('rows') as FormArray;
  }

  disabledWaiverCheckbox(){
    this.driverNameForm.controls["waiverInd"].disable();
    this.driverNameForm.controls["waiverInd"].setValue(true);
    this.isDisableAddDriverBtn = true;
  }

  disabledWaiverWOCheckbox(){
    this.driverNameForm.controls["waiverInd"].disable();
    this.driverNameForm.controls["waiverInd"].setValue(false);
    this.isDisableAddDriverBtn = true;
  }

  disabledAllDriverCheckbox(){
    this.driverNameForm.controls["allDriverInd"].disable();
    this.driverNameForm.controls["allDriverInd"].setValue(true);
    this.isDisableAddDriverBtn = true;
  }

  enabledWaiverCheckbox(){
    this.driverNameForm.controls["waiverInd"].enable();
    let allDriverInd = this.driverNameForm.controls['allDriverInd'].value;
    let waiverInd = this.driverNameForm.controls['waiverInd'].value;
    if(!allDriverInd) { //AGIC-49648
      this.isDisableAddDriverBtn = false;
    }

    if(this.isPrivateCarsExcludingGoods && this.isComprehensive) {
      this.isDisableAddDriverBtn = waiverInd ? true : false;
    }

    this.isDisableAddDriverBtn = this.disableAddDriver(); //AGIC-50673
  }

  enabledAllDriverCheckbox(){
    this.driverNameForm.controls["allDriverInd"].enable();
  }

  tickWaiverCheckbox(isWaiver: boolean){
    this.driverNameForm.controls["waiverInd"].enable();
    this.driverNameForm.controls['waiverInd'].setValue(isWaiver);
    this.isDisableAddDriverBtn = this.disableAddDriver();
  }

}
